import React, { useState, useEffect } from 'react';

export default function CookieConsent() {
  const [showConsent, setShowConsent] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true, // Always required
    analytics: false,
    marketing: false,
    functional: false,
    thirdParty: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consentGiven = localStorage.getItem('cookieConsent');
    if (!consentGiven) {
      setShowConsent(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: true,
      functional: true,
      thirdParty: true
    };
    
    setCookiePreferences(allAccepted);
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowConsent(false);
    
    // Enable all cookies
    enableCookies(allAccepted);
  };

  const handleAcceptEssential = () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      marketing: false,
      functional: false,
      thirdParty: false
    };
    
    setCookiePreferences(essentialOnly);
    localStorage.setItem('cookieConsent', JSON.stringify(essentialOnly));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowConsent(false);
    
    // Enable only essential cookies
    enableCookies(essentialOnly);
  };

  const handleCustomPreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(cookiePreferences));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowConsent(false);
    
    // Enable cookies based on preferences
    enableCookies(cookiePreferences);
  };

  const enableCookies = (preferences) => {
    // Essential cookies (always enabled)
    if (preferences.essential) {
      // Session management, security, basic functionality
      console.log('Essential cookies enabled');
    }

    // Analytics cookies
    if (preferences.analytics) {
      // Google Analytics, user behavior tracking
      console.log('Analytics cookies enabled');
      // Initialize Google Analytics here
    }

    // Marketing cookies
    if (preferences.marketing) {
      // Advertising, retargeting, social media pixels
      console.log('Marketing cookies enabled');
      // Initialize Facebook Pixel, Google Ads, etc.
    }

    // Functional cookies
    if (preferences.functional) {
      // Enhanced features, preferences, personalization
      console.log('Functional cookies enabled');
    }

    // Third-party cookies
    if (preferences.thirdParty) {
      // External services, embeds, social media
      console.log('Third-party cookies enabled');
    }
  };

  const updatePreference = (type, value) => {
    setCookiePreferences(prev => ({
      ...prev,
      [type]: value
    }));
  };

  if (!showConsent) {
    return null;
  }

  return (
    <div className="cookie-consent-overlay">
      <div className="cookie-consent-modal">
        <div className="cookie-header">
          <h3>🍪 Cookie Preferences</h3>
          <p>We use cookies to enhance your experience on GradedWash</p>
        </div>

        {!showDetails ? (
          <div className="cookie-simple">
            <div className="cookie-description">
              <p>
                We use cookies to provide essential functionality, analyze site usage, 
                and deliver personalized content. Some cookies are necessary for the 
                site to work properly, while others help us improve your experience.
              </p>
            </div>

            <div className="cookie-actions">
              <button 
                className="btn btn-secondary"
                onClick={handleAcceptEssential}
              >
                Accept Essential Only
              </button>
              <button 
                className="btn btn-outline"
                onClick={() => setShowDetails(true)}
              >
                Customize Preferences
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleAcceptAll}
              >
                Accept All Cookies
              </button>
            </div>
          </div>
        ) : (
          <div className="cookie-detailed">
            <div className="cookie-categories">
              <div className="cookie-category">
                <div className="category-header">
                  <div className="category-info">
                    <h4>Essential Cookies</h4>
                    <p>Required for the website to function properly</p>
                  </div>
                  <div className="category-toggle">
                    <input
                      type="checkbox"
                      checked={cookiePreferences.essential}
                      disabled
                      readOnly
                    />
                    <span className="toggle-label">Always Active</span>
                  </div>
                </div>
                <div className="category-details">
                  <ul>
                    <li>Session management and security</li>
                    <li>Basic site functionality</li>
                    <li>User authentication</li>
                    <li>Payment processing</li>
                  </ul>
                </div>
              </div>

              <div className="cookie-category">
                <div className="category-header">
                  <div className="category-info">
                    <h4>Analytics Cookies</h4>
                    <p>Help us understand how visitors use our site</p>
                  </div>
                  <div className="category-toggle">
                    <input
                      type="checkbox"
                      checked={cookiePreferences.analytics}
                      onChange={(e) => updatePreference('analytics', e.target.checked)}
                    />
                    <span className="toggle-label">Optional</span>
                  </div>
                </div>
                <div className="category-details">
                  <ul>
                    <li>Google Analytics for site usage</li>
                    <li>Performance monitoring</li>
                    <li>User behavior analysis</li>
                    <li>Improvement insights</li>
                  </ul>
                </div>
              </div>

              <div className="cookie-category">
                <div className="category-header">
                  <div className="category-info">
                    <h4>Marketing Cookies</h4>
                    <p>Used for advertising and promotional purposes</p>
                  </div>
                  <div className="category-toggle">
                    <input
                      type="checkbox"
                      checked={cookiePreferences.marketing}
                      onChange={(e) => updatePreference('marketing', e.target.checked)}
                    />
                    <span className="toggle-label">Optional</span>
                  </div>
                </div>
                <div className="category-details">
                  <ul>
                    <li>Facebook Pixel for advertising</li>
                    <li>Google Ads tracking</li>
                    <li>Retargeting campaigns</li>
                    <li>Social media integration</li>
                  </ul>
                </div>
              </div>

              <div className="cookie-category">
                <div className="category-header">
                  <div className="category-info">
                    <h4>Functional Cookies</h4>
                    <p>Enable enhanced features and personalization</p>
                  </div>
                  <div className="category-toggle">
                    <input
                      type="checkbox"
                      checked={cookiePreferences.functional}
                      onChange={(e) => updatePreference('functional', e.target.checked)}
                    />
                    <span className="toggle-label">Optional</span>
                  </div>
                </div>
                <div className="category-details">
                  <ul>
                    <li>User preferences and settings</li>
                    <li>Enhanced travel matching</li>
                    <li>Personalized recommendations</li>
                    <li>Language and region settings</li>
                  </ul>
                </div>
              </div>

              <div className="cookie-category">
                <div className="category-header">
                  <div className="category-info">
                    <h4>Third-Party Cookies</h4>
                    <p>Set by external services and partners</p>
                  </div>
                  <div className="category-toggle">
                    <input
                      type="checkbox"
                      checked={cookiePreferences.thirdParty}
                      onChange={(e) => updatePreference('thirdParty', e.target.checked)}
                    />
                    <span className="toggle-label">Optional</span>
                  </div>
                </div>
                <div className="category-details">
                  <ul>
                    <li>Stripe payment processing</li>
                    <li>Social media embeds</li>
                    <li>Travel booking partners</li>
                    <li>External content providers</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="cookie-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => setShowDetails(false)}
              >
                Back to Simple View
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleCustomPreferences}
              >
                Save Preferences
              </button>
            </div>
          </div>
        )}

        <div className="cookie-footer">
          <p>
            By using our site, you agree to our{' '}
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>{' '}
            and{' '}
            <a href="/terms-of-service" target="_blank" rel="noopener noreferrer">
              Terms of Service
            </a>
            . You can change your cookie preferences at any time in your account settings.
          </p>
        </div>
      </div>
    </div>
  );
}

// Cookie management utilities
export const getCookieConsent = () => {
  const consent = localStorage.getItem('cookieConsent');
  return consent ? JSON.parse(consent) : null;
};

export const hasCookieConsent = (type) => {
  const consent = getCookieConsent();
  return consent ? consent[type] : false;
};

export const updateCookieConsent = (newPreferences) => {
  localStorage.setItem('cookieConsent', JSON.stringify(newPreferences));
  localStorage.setItem('cookieConsentDate', new Date().toISOString());
}; 